import {
  AppCard,
  Button,
  Icon,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiPencil } from "@mdi/js";
import { Box, CircularProgress, Typography } from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  getShowIssuesOfRfqRequestQueryKey,
  useShowIssuesOfRfqRequest,
} from "../../../../../__generated__/orval/endpoints/issues-of-rfq-requests/issues-of-rfq-requests";
import { MmHistoryLogs } from "../../../../../components/molecules/MmHistoryLogs";
import { MmRequestAttachments } from "../../../../../components/molecules/MmRequestAttachments";
import { MmRequestHeader } from "../../../../../components/molecules/MmRequestHeader";
import { MmRequestComments } from "../../../../../components/molecules/MmRequestsComments";
import {
  MmRequestTabs,
  type MmRequestTabValue,
} from "../../../../../components/molecules/MmRequestTabs";
import { useAuth } from "../../../../../hooks/useAuth";
import { MmRequestChildDetails } from "../../mm_requests/-internal-components/MmRequestChildDetails";
import { mmRequestTabsSearchSchema } from "../../mm_requests/-schemas/mmRequestTabsSearchSchema";
import {
  IssuesOfRfqDetailsFieldsAfterDescription,
  IssuesOfRfqDetailsFieldsBeforeDescription,
} from "../-internal-components/IssuesOfRfqDetailsFields";

const ShowIssuesOfRfqRequest: React.FC = () => {
  const { t } = useTranslation();
  const navigate = Route.useNavigate();
  const { user } = useAuth();
  const { issuesOfRfqRequestId } = Route.useParams();
  const { tab } = Route.useSearch();
  const id = Number(issuesOfRfqRequestId);

  const { data, isLoading, isError } = useShowIssuesOfRfqRequest(id);

  useEffect(() => {
    if (isError) {
      enqueueSnackbar(t("mmRequests.fetchError"), { variant: "error" });
    }
  }, [isError, t]);

  const handleTabChange = useCallback(
    (newValue: MmRequestTabValue) => {
      if (newValue === "materialList") return;

      navigate({
        replace: true,
        resetScroll: false,
        search: (prev) => ({
          ...prev,
          tab: newValue,
        }),
      });
    },
    [navigate],
  );

  const handleEdit = useCallback(() => {
    navigate({
      params: { issuesOfRfqRequestId },
      to: "/vbuyer/issues_of_rfq_requests/$issuesOfRfqRequestId/edit",
    });
  }, [issuesOfRfqRequestId, navigate]);

  const mmRequest = data?.mm_request;
  const mmRequestId = mmRequest?.id;
  const isCompleted = mmRequest?.process_instance?.completed ?? false;

  const userCanEdit = useMemo(() => {
    if (!user || !mmRequest) return false;

    return (
      (user.roles_names.includes("Edit material requests") ||
        user.id === mmRequest.assigned_to?.id) &&
      !mmRequest.process_instance?.completed
    );
  }, [mmRequest, user]);

  if (isLoading) {
    return (
      <PageContainer
        showBackButton
        title={`${t("common.issuesOfRfq")} #${mmRequestId ?? ""}`}
      >
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      showBackButton
      title={`${t("common.issuesOfRfq")} #${mmRequestId ?? ""}`}
    >
      {!!mmRequest && (
        <MmRequestHeader
          id={mmRequest.id}
          keysToInvalidate={[...getShowIssuesOfRfqRequestQueryKey(id)]}
        />
      )}

      <AppCard>
        <MmRequestTabs onChange={handleTabChange} value={tab} />
        <Box sx={{ marginTop: "32px" }}>
          {tab === "details" && mmRequest && (
            <AppCard
              header={
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="h6">
                    {t("common.requestDetails")}
                  </Typography>
                  {userCanEdit && (
                    <Button
                      onClick={handleEdit}
                      startIcon={<Icon icon={mdiPencil} />}
                      variant="outlined"
                    >
                      {t("common.edit")}
                    </Button>
                  )}
                </Box>
              }
            >
              <MmRequestChildDetails
                childrenAfterDescription={
                  <IssuesOfRfqDetailsFieldsAfterDescription
                    purchaseOrder={data?.purchase_order}
                    purchaseRequisition={data?.purchase_requisition}
                    technicalContact={data?.technical_contact}
                    typeValue={data?.identified_problem_type?.title}
                    valueCurrent={data?.value_current}
                    valueNew={data?.value_new}
                    vendorName={data?.vendor_name}
                    workOrderNumber={data?.work_order_number ?? undefined}
                    workOrderResponsiblePersonName={
                      data?.work_order_responsible_person?.full_name_with_login
                    }
                  />
                }
                childrenBeforeDescription={
                  <IssuesOfRfqDetailsFieldsBeforeDescription
                    closingDate={data?.closing_date}
                    discipline={data?.discipline}
                    m5={data?.m5}
                    openingDate={data?.opening_date}
                    ritm={data?.ritm}
                    serviceNowReason={data?.service_now_reason}
                    stepPurchase={data?.step_purchase}
                  />
                }
                mmRequest={mmRequest}
                onEdit={handleEdit}
                renderChildrenBeforeDescriptionAfterDescription
                showCreatedUpdated={false}
                showEditButton={false}
                showMaterialNumber
                showPreviousTicket
                showRequesterArea={false}
              />
            </AppCard>
          )}
          {tab === "attachments" && mmRequestId && (
            <MmRequestAttachments
              isCompleted={isCompleted}
              mmRequestId={mmRequestId}
            />
          )}
          {tab === "comments" && mmRequestId && (
            <MmRequestComments
              isCompleted={isCompleted}
              mmRequestId={mmRequestId}
            />
          )}
          {tab === "history" && mmRequestId && (
            <MmHistoryLogs mmRequestId={mmRequestId} />
          )}
        </Box>
      </AppCard>
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/issues_of_rfq_requests/$issuesOfRfqRequestId/",
)({
  component: ShowIssuesOfRfqRequest,
  validateSearch: mmRequestTabsSearchSchema,
});
